import { CustomModal, FormInput, FormSelector, FormUpload } from "@/Components";
import { useCreateFleet } from "@/hooks/companiesHook";
import { useRetrieveTitle } from "@/Utils/helpers";
import { Col, Form, FormInstance, Radio, Row, Switch } from "antd";
import { get } from "lodash";
import React, { useState } from "react";
import { toast } from "react-toastify";
import style from "./style.module.scss";
import FormCheckboxGroup from "@/Components/Form/FormCheckboxGroup";
import { getCookie } from "cookies-next";
import { useRegions } from "@/hooks/employeesHook";
import { RegionsProps } from "@/types";
import { getTitleLanguage } from "@/Utils/orderHelpers";

const FleetModel: React.FC<any> = ({ open, setOpen, services }) => {
  const { getTitle, language } = useRetrieveTitle();
  const [form] = Form.useForm<FormInstance>();
  const [nameLang, setNameLang] = useState<string>("en-us");
  const [sloganLang, setSloganLang] = useState<string>("en-us");
  const [getFormValues, setFormValues] = useState<Record<string, any>>({});
  const dotServiceId: any = getCookie("serviceIdDot");
  const { mutateAsync: createFleet, isPending: isFleetPending } =
    useCreateFleet();

  const { data: regionsRes } = useRegions();

  const regions = get(regionsRes, "data.data", []);
  const regionsOptions = regions.map(({ iso2, country_name }) => ({
    label: getTitleLanguage(country_name, language) || "",
    value: iso2,
  }));

  const handleSubmit = async (value: any) => {
    const imgUrl = get(value, "logo.file.response.data.browserUrl");
    const { service_ids, logo_scale, iso2, url } = value;
    const payload = {
      name: {
        "en-us": getFormValues["en-us"] || "",
        "ar-sa": getFormValues["ar-sa"] || "",
      },
      logo: imgUrl,
      slogan: {
        "en-us": getFormValues["slogan-en"] || "",
        "ar-sa": getFormValues["slogan-sa"] || "",
      },
      service_ids,
      logo_scale,
      iso2,
      // tags,
      url,
    };
    try {
      const res = await createFleet(payload);
      if (res) {
        toast.success(getTitle("fleet_created_successfully"));
        form.resetFields();
        setOpen(false);
      }
    } catch (error) {
      toast.error(getTitle("data_failed"));
      console.log(error);
    }
  };

  return (
    <CustomModal
      open={open}
      handleCancel={() => {
        setOpen(false);
        form.resetFields();
        setFormValues({});
      }}
      handleOk={form.submit}
      confirmLoading={isFleetPending}
      width={1100}
      okText={getTitle("create")}
      cancelText={getTitle("cancel")}
    >
      <Form
        initialValues={{ service_ids: [dotServiceId], logo_scale: "fill" }}
        onFinish={handleSubmit}
        onValuesChange={(values) => {
          setFormValues({ ...getFormValues, ...values });
        }}
        autoComplete="off"
        form={form}
      >
        <Row className={style.border}>
          <Col xs={8} sm={12} md={12} lg={6} className={style.createfleet}>
            <h2>{getTitle("create_fleet")}</h2>
          </Col>
          <Col
            xs={16}
            sm={12}
            md={12}
            lg={18}
            className={style.createfleetSwitch}
          >
            <span className={style.createfleetSwitch}>
              <p className={style.margin}>{getTitle("active")}</p>
              <Switch />
            </span>
          </Col>
        </Row>
        <Row className={style.border}>
          <Col xs={24} sm={24} md={24} lg={6} className={style.uploadWrapper}>
            <Form.Item name="logo" className={style.formitem}>
              <FormUpload />
            </Form.Item>
          </Col>
          <Col
            xs={24}
            sm={12}
            md={12}
            lg={9}
            className={style.createfleetInput}
          >
            {nameLang === "en-us" && (
              <Form.Item
                name={["en-us"]}
                className={style.formitem}
                rules={[
                  {
                    required: true,
                    message: getTitle("name_cannot_be_empty"),
                  },
                ]}
              >
                <FormInput
                  placeholder={getTitle("enter_name")}
                  title={getTitle("name")}
                  langBtn={true}
                  setSelectedLang={setNameLang}
                  selectedLang={nameLang}
                />
              </Form.Item>
            )}
            {nameLang === "ar-sa" && (
              <Form.Item
                name={["ar-sa"]}
                className={style.formitem}
                rules={[
                  {
                    required: true,
                    message: getTitle("name_cannot_be_empty"),
                  },
                ]}
              >
                <FormInput
                  placeholder={getTitle("enter_name")}
                  title={getTitle("name")}
                  langBtn={true}
                  setSelectedLang={setNameLang}
                  selectedLang={nameLang}
                />
              </Form.Item>
            )}
            <Form.Item name="url" className={style.formitem}>
              <FormInput
                title={getTitle("url")}
                placeholder={getTitle("enter_url")}
              />
            </Form.Item>
          </Col>
          <Col
            xs={24}
            sm={12}
            md={12}
            lg={9}
            className={style.createfleetInput}
          >
            {sloganLang === "en-us" && (
              <Form.Item name={["slogan-en"]} className={style.formitem}>
                <FormInput
                  title={getTitle("slogan")}
                  placeholder={getTitle("enter_slogan")}
                  langBtn={true}
                  setSelectedLang={setSloganLang}
                  selectedLang={sloganLang}
                />
              </Form.Item>
            )}
            {sloganLang === "ar-sa" && (
              <Form.Item name={["slogan-sa"]} className={style.formitem}>
                <FormInput
                  title={getTitle("slogan")}
                  placeholder={getTitle("enter_slogan")}
                  langBtn={true}
                  setSelectedLang={setSloganLang}
                  selectedLang={sloganLang}
                />
              </Form.Item>
            )}
            <Form.Item
              name="iso2"
              rules={[
                {
                  required: true,
                  message: getTitle("region_can_not_be_empty"),
                },
              ]}
              className={style.formitem}
            >
              <FormSelector
                title={getTitle("select_region")}
                placeholder={getTitle("select_region")}
                options={regionsOptions}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[0, 0]}>
          <Col span={6} className={style.fillWrapper}>
            <Form.Item name="logo_scale" className={style.formWrap}>
              <Radio.Group>
                <Radio value={"fill"}>Fill</Radio>
                <Radio value={"fit"}>Fit</Radio>
              </Radio.Group>
            </Form.Item>
          </Col>
          <Col span={18} className={style.checkboxScroll}>
            <Form.Item name="service_ids" className={style.formWrapCheck}>
              <FormCheckboxGroup
                defaultChecked={dotServiceId}
                options={services}
                onChange={(value) => { }}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </CustomModal>
  );
};

export default FleetModel;
